import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import apiCacheService from '../services/ApiCacheService';
import apiMonitor from '../utils/apiMonitor';

/**
 * Debug component for API cache management
 * Only shows in development mode
 */
const DebugApiCache = () => {
  const [cacheStats, setCacheStats] = useState(null);
  const [monitorStats, setMonitorStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const updateStats = () => {
    const cacheStats = apiCacheService.getCacheStats();
    const monitorStats = apiMonitor.getStats();
    setCacheStats(cacheStats);
    setMonitorStats(monitorStats);
    setLastUpdate(new Date().toLocaleTimeString());
  };

  useEffect(() => {
    updateStats();
    const interval = setInterval(updateStats, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const handleClearCache = () => {
    apiCacheService.clearCache();
    updateStats();
  };

  const handleClearSnapshotCache = () => {
    apiCacheService.clearCacheEntry('snapshot-months');
    updateStats();
  };

  const handleClearMonitor = () => {
    apiMonitor.clear();
    updateStats();
  };

  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
      <Paper elevation={8} sx={{ p: 2, maxWidth: 400, maxHeight: 500, overflow: 'auto' }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" color="primary">
              🐛 API Cache Debug
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Alert severity="info" variant="outlined">
                Last updated: {lastUpdate}
              </Alert>

              {cacheStats && (
                <>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Cache Statistics:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip 
                        label={`Cache: ${cacheStats.cacheSize}`} 
                        color="primary" 
                        size="small" 
                      />
                      <Chip 
                        label={`Pending: ${cacheStats.pendingRequests}`} 
                        color="secondary" 
                        size="small" 
                      />
                    </Stack>
                  </Box>

                  {monitorStats && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        API Monitor Statistics:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip 
                          label={`Total: ${monitorStats.totalCalls}`} 
                          color="info" 
                          size="small" 
                        />
                        <Chip 
                          label={`Success: ${monitorStats.successfulCalls}`} 
                          color="success" 
                          size="small" 
                        />
                        <Chip 
                          label={`Failed: ${monitorStats.failedCalls}`} 
                          color="error" 
                          size="small" 
                        />
                        <Chip 
                          label={`Avg: ${monitorStats.avgDuration}ms`} 
                          color="warning" 
                          size="small" 
                        />
                      </Stack>
                    </Box>
                  )}

                  {monitorStats && monitorStats.problematicUrls && monitorStats.problematicUrls.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom color="error">
                        ⚠️ Problematic URLs:
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>URL</TableCell>
                              <TableCell>Calls</TableCell>
                              <TableCell>Avg Duration</TableCell>
                              <TableCell>Issue</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {monitorStats.problematicUrls.slice(0, 5).map((url, index) => (
                              <TableRow key={index}>
                                <TableCell sx={{ fontSize: '0.7rem' }}>{url.url}</TableCell>
                                <TableCell>{url.count}</TableCell>
                                <TableCell>{Math.round(url.avgDuration)}ms</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={url.issue} 
                                    size="small" 
                                    color={url.issue === 'High frequency' ? 'error' : 'warning'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Cached Keys:
                    </Typography>
                    <Stack spacing={0.5}>
                      {cacheStats.cacheKeys.length > 0 ? (
                        cacheStats.cacheKeys.map((key, index) => (
                          <Chip
                            key={index}
                            label={key}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No cached data
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      startIcon={<RefreshIcon />}
                      onClick={updateStats}
                      size="small"
                      variant="outlined"
                    >
                      Refresh
                    </Button>
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={handleClearSnapshotCache}
                      size="small"
                      variant="outlined"
                      color="warning"
                    >
                      Clear Snapshot
                    </Button>
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={handleClearMonitor}
                      size="small"
                      variant="outlined"
                      color="info"
                    >
                      Clear Monitor
                    </Button>
                    <Button
                      startIcon={<ClearIcon />}
                      onClick={handleClearCache}
                      size="small"
                      variant="outlined"
                      color="error"
                    >
                      Clear All
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
};

export default DebugApiCache;
